import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import PaymentReceiptModal from "./PaymentReceiptModal";
import s from "./ChargePaymentsModal.module.css";

/**
 * Per-charge payment-history feature — a small reusable trigger button
 * (ChargeInfoButton) that opens a portal-rendered modal
 * (ChargePaymentsModal) listing every receipt recorded against one
 * AdditionalCharge.
 *
 * Source of truth: GET /api/additional-charges/{id}/payments — backed
 * by the AdditionalChargePayment table, one row per "I'm collecting
 * ₱X today" event recorded via POST /api/additional-charges/{id}/pay.
 *
 * Auth: the GET endpoint is gated to Admin / SuperAdmin / Cashier
 * (AdminGuard.requireAdmin). Callers in parent / student-facing
 * surfaces should NOT render the trigger button until that gate is
 * widened with an ownership check, otherwise the user gets a 403
 * when they click. StatementOfAccountPage already gates visibility
 * by role for this reason.
 *
 * Why a separate file: the same trigger + modal is reused from three
 * surfaces (admin AdditionalChargesModal, admin HistoryModal, and
 * the SOA page). Extracting keeps styling and a11y plumbing in one
 * place so a future visual tweak ripples to all three at once, and
 * also lets non-tuition surfaces import without dragging in the
 * Tuition admin page's CSS module.
 */

function fmtMoney(n) {
  return Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

/* Tiny inline (i) button. Stops click propagation so it doesn't
 * trigger any parent row's onClick. The label prop drives both
 * aria-label and tooltip so screen-reader and pointer users get
 * the same context. */
export function ChargeInfoButton({ onClick, label, disabled }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      disabled={disabled}
      aria-label={label || "View payment history"}
      title={label || "View payment history"}
      className={s.infoBtn}
    >
      i
    </button>
  );
}

/* Per-charge payment history modal. Read-only.
 *
 * Props:
 *   - charge:  the AdditionalCharge row {id, label, amount, paidAmount, status, ...}
 *   - student: optional {firstName, lastName, studentNumber} for the header subtitle
 *   - onClose: dismiss handler
 *
 * Behaviour:
 *   - Loads payments from GET /api/additional-charges/{id}/payments on mount.
 *   - Esc / overlay click / Close button all dismiss.
 *   - Body scroll locked while open.
 *   - Cards cascade in with a small per-row delay; reduced-motion users
 *     get the same panel without the cascade.
 */
export function ChargePaymentsModal({ charge, student, schoolYearLabel, onClose }) {
  const [payments, setPayments]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState("");
  // Receipt-modal target — set when the user clicks "Receipt" on a
  // payment row. Renders PaymentReceiptModal as a portal above this
  // modal so the receipt isn't constrained by ChargePaymentsModal's
  // scroll area.
  const [receiptTarget, setReceiptTarget] = useState(null);

  // Headline numbers come from the parent charge so the modal stays in
  // lock-step with the SOA values even if a per-row sum drifts (e.g.
  // an admin override via PUT /{id} that doesn't write a history row).
  const amountTotal = Number(charge?.amount || 0);
  const paidTotal   = Number(charge?.paidAmount || 0);
  const remaining   = Math.max(amountTotal - paidTotal, 0);
  const remainingClear = remaining <= 0.005;

  useEffect(() => {
    if (!charge?.id) return;
    let cancelled = false;
    setLoading(true); setLoadError("");
    fetch(`${API_BASE}/api/additional-charges/${charge.id}/payments`)
      .then(async r => {
        if (!r.ok) {
          throw new Error(await extractErrorMessage(r, `Load failed (${r.status})`));
        }
        return r.json();
      })
      .then(rows => { if (!cancelled) setPayments(Array.isArray(rows) ? rows : []); })
      .catch(e => { if (!cancelled) setLoadError(e.message || "Could not load payments."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [charge?.id]);

  // Esc to close + body-scroll lock while open.
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); }
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  if (!charge) return null;

  const studentName = student
    ? `${student.lastName || ""}, ${student.firstName || ""}`.replace(/^, |, $/g, "")
    : "—";

  const status = charge.status || "UNPAID";
  const statusClass =
      status === "PAID"    ? s.statusPillPAID
    : status === "PARTIAL" ? s.statusPillPARTIAL
    :                        s.statusPillUNPAID;
  const statusLabel =
      status === "PAID"    ? "Paid"
    : status === "PARTIAL" ? "Partial"
    :                        "Unpaid";

  function methodChip(m) {
    const cls =
        m === "CASH"  ? s.methodChipCASH
      : m === "BANK"  ? s.methodChipBANK
      : m === "GCASH" ? s.methodChipGCASH
      : m === "CARD"  ? s.methodChipCARD
      :                 s.methodChipOTHER;
    return <span className={`${s.methodChip} ${cls}`}>{m || "—"}</span>;
  }

  return (
    <>
    {createPortal(
    <div className={s.overlay}>
      <div className={s.modal} onClick={(e) => e.stopPropagation()}>
        <div className={s.head}>
          <h3 className={s.title}>
            <span aria-hidden>📜</span>
            Payments for this charge
          </h3>
          <button className={s.close}
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  autoFocus>
            ×
          </button>
        </div>

        <div className={s.sub}>
          <div className={s.subTopRow}>
            <div className={s.subName}>{charge.label}</div>
            <span className={`${s.statusPill} ${statusClass}`}>{statusLabel}</span>
          </div>
          <div className={s.subMeta}>
            <span>{studentName}</span>
            {student?.studentNumber && <span> · #{student.studentNumber}</span>}
          </div>
        </div>

        {/* Stat strip — Charge / Paid / Remaining. */}
        <div className={s.statStrip}>
          <div className={`${s.statBox} ${s.statBoxAmount}`}>
            <div className={`${s.statLabel} ${s.statLabelAmount}`}>Charge</div>
            <div className={`${s.statValue} ${s.statValueAmount}`}>
              ₱{fmtMoney(amountTotal)}
            </div>
          </div>
          <div className={`${s.statBox} ${s.statBoxPaid}`}>
            <div className={`${s.statLabel} ${s.statLabelPaid}`}>Paid</div>
            <div className={`${s.statValue} ${s.statValuePaid}`}>
              ₱{fmtMoney(paidTotal)}
            </div>
          </div>
          <div className={`${s.statBox} ${remainingClear ? s.statBoxRemainingClear : s.statBoxRemaining}`}>
            <div className={`${s.statLabel} ${remainingClear ? s.statLabelRemainingClear : s.statLabelRemaining}`}>
              Remaining
            </div>
            <div className={`${s.statValue} ${remainingClear ? s.statValueRemainingClear : s.statValueRemaining}`}>
              ₱{fmtMoney(remaining)}
            </div>
          </div>
        </div>

        <div className={s.sectionHead}>
          Receipts on file
          <span className={s.sectionHeadCount}>
            {loading ? "loading…"
              : payments.length === 0 ? "no payments recorded yet"
              : `${payments.length} ${payments.length === 1 ? "receipt" : "receipts"}`}
          </span>
        </div>

        {loadError && <div className={s.error}>{loadError}</div>}

        <div className={s.list}>
          {loading ? (
            <div className={s.loadingRow}>Loading payment history…</div>
          ) : payments.length === 0 ? (
            <div className={s.empty}>
              <div className={s.emptyIcon} aria-hidden>🧾</div>
              <div className={s.emptyTitle}>No payments recorded yet</div>
              <div className={s.emptyHint}>
                Use the <b>Pay</b> button on the charge to record the first
                receipt. Each payment captures its own reference number.
              </div>
            </div>
          ) : (
            payments.map((p, i) => {
              const accent =
                  p.method === "CASH"  ? "#1f6a2d"
                : p.method === "BANK"  ? "#1565c0"
                : p.method === "GCASH" ? "#006bb6"
                : p.method === "CARD"  ? "#6a1b9a"
                :                        "#8a6d28";
              return (
                <div
                  key={p.id}
                  className={s.card}
                  style={{
                    borderLeft: `3px solid ${accent}`,
                    animationDelay: `${Math.min(i * 50, 350)}ms`,
                  }}
                >
                  <div className={s.cardLeft}>
                    <div className={s.cardAmount}>₱{fmtMoney(p.amount)}</div>
                    <div className={s.cardMethodWrap}>
                      {methodChip(p.method)}
                      {/* Bank name only renders for BANK rows. Sits below
                          the method chip so the channel and the issuing
                          bank read together at a glance. */}
                      {p.method === "BANK" && p.bankName && (
                        <div className={s.cardBankName} title={p.bankName}>
                          {p.bankName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={s.cardRight}>
                    <div className={s.cardRefRow}>
                      <span className={s.cardRefLabel}>Ref</span>
                      <span className={s.cardRefValue}>{p.referenceNo || "—"}</span>
                    </div>
                    <div className={s.cardMeta}>
                      <span>{fmtDateTime(p.paidAt)}</span>
                      {p.recordedByUsername && (
                        <span className={s.cardMetaBy}>
                          · by{" "}
                          <b className={s.cardMetaByName}>{p.recordedByUsername}</b>
                          {p.recordedByRole && (
                            <span className={s.cardMetaByRole}> ({p.recordedByRole})</span>
                          )}
                        </span>
                      )}
                    </div>
                    {p.notes && (
                      <div className={s.cardNote}>
                        <b className={s.cardNoteLabel}>Note</b>
                        {p.notes}
                      </div>
                    )}
                    {/* Per-receipt action — opens PaymentReceiptModal
                        with this row's data, the parent charge, and
                        the student so a polished mini receipt can be
                        printed or downloaded as a PDF. */}
                    <div className={s.cardActions}>
                      <button
                        type="button"
                        className={s.receiptBtn}
                        onClick={() => setReceiptTarget(p)}
                        title="Open this payment's official receipt"
                        aria-label={`Open receipt for ₱${fmtMoney(p.amount)} ref ${p.referenceNo || ""}`}
                      >
                        <span aria-hidden>🧾</span>
                        <span>Receipt</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className={s.actions}>
          <button type="button" className={s.btnGhost} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
    )}

    {/* Per-payment "Official Receipt" overlay — opens above this modal
        when the user clicks Receipt on a card. Stays as a sibling so
        the card list keeps scrolling smoothly behind it. */}
    {receiptTarget && (
      <PaymentReceiptModal
        payment={receiptTarget}
        charge={charge}
        student={student}
        schoolYearLabel={schoolYearLabel}
        onClose={() => setReceiptTarget(null)}
      />
    )}
    </>
  );
}
